import { useEffect, useState } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PasswordField } from "@/components/ui/PasswordField";
import { ApiError } from "@/lib/api";
import { useLogin, useMe } from "@/queries/auth";
import { useAuthStore } from "@/stores/auth";

const loginSearchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: loginSearchSchema,
});

function LoginPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const accessToken = useAuthStore((s) => s.accessToken);
  const { data: me } = useMe(Boolean(accessToken));
  const login = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (accessToken && me) {
      const target = me.must_change_password ? "/force-change-password" : (search.next ?? "/world");
      void navigate({ to: target });
    }
  }, [accessToken, me, navigate, search.next]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorKey(null);
    login.mutate(
      { email, password },
      {
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.status === 400 || err.status === 401) setErrorKey("invalidCredentials");
            else if (err.status === 429) setErrorKey("rateLimited");
            else setErrorKey("generic");
          } else {
            setErrorKey("generic");
          }
        },
      },
    );
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t("login.title")}</CardTitle>
        <CardDescription>{t("login.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor="login-email">{t("login.emailLabel")}</Label>
            <Input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              required
              placeholder={t("login.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={login.isPending}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="login-password">{t("login.passwordLabel")}</Label>
            <PasswordField
              id="login-password"
              name="password"
              autoComplete="current-password"
              required
              placeholder={t("login.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={login.isPending}
            />
          </div>
          {errorKey && (
            <Alert variant="destructive">
              <AlertDescription>{t(`login.errors.${errorKey}`)}</AlertDescription>
            </Alert>
          )}
          <Button type="submit" disabled={login.isPending || !email || !password}>
            {login.isPending ? t("login.submitting") : t("login.submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
